import { useState, useEffect, useRef } from 'react';

import {
  useCloudflareImage,
  useResponsiveCloudflareImage,
} from '@/hooks/useCloudflareImage';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
import {
  IMAGE_QUALITY,
  PLACEHOLDER_BLUR,
  generateInitials,
  hasValidAvatar,
  createResponsiveBreakpoints,
  getImageSizes,
} from './constants';
import { cn } from '@simplywallst/ui-core/next';

const avatar = tv({
  base: 'border relative grid content-center items-center justify-center overflow-hidden rounded-full border-soft bg-p-white-170 ',
  variants: {
    size: {
      micro: 'size-x2 text-micro',
      xs: 'size-6 text-xs',
      sm: 'size-8 text-sm',
      md: 'size-12 text-base',
      lg: 'size-14 text-lg',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

type AvatarProps = VariantProps<typeof avatar>;

type Props = {
  owner: {
    displayName: string | null | undefined;
    imageUrl: string | null | undefined;
  };
  className?: string;
  priority?: boolean;
} & AvatarProps;

// Extracted component for progressive image loading
const ProgressiveImage = ({
  imageUrl,
  mobileSize,
  tabletSize,
  desktopSize,
  displayName,
  priority,
}: {
  imageUrl: string;
  mobileSize: number;
  tabletSize: number;
  desktopSize: number;
  displayName: string | null | undefined;
  priority: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  const responsiveBreakpoints = createResponsiveBreakpoints(
    mobileSize,
    tabletSize,
    desktopSize
  );

  const { srcSet } = useResponsiveCloudflareImage({
    src: imageUrl,
    breakpoints: responsiveBreakpoints,
  });

  const placeholderUrl = useCloudflareImage({
    src: imageUrl,
    width: mobileSize,
    height: mobileSize,
    quality: IMAGE_QUALITY.placeholder,
    blur: PLACEHOLDER_BLUR,
  });

  const mobileImageUrl = useCloudflareImage({
    src: imageUrl,
    width: mobileSize,
    height: mobileSize,
    quality: IMAGE_QUALITY.mobile,
  });

  const sizes = `${mobileSize}px`;

  useEffect(() => {
    // Check if image is already loaded from cache
    if (imgRef.current?.complete) {
      setLoaded(true);
      setShowPlaceholder(false);
    }
  }, []);

  const handleImageLoad = () => {
    setLoaded(true);
    setShowPlaceholder(false);
  };

  const handleImageError = () => {
    setLoaded(false);
    setShowPlaceholder(false);
  };

  return (
    <div className="absolute inset-0">
      {/* Placeholder image for progressive loading */}
      {showPlaceholder && placeholderUrl && (
        <img
          src={placeholderUrl}
          alt=""
          className="absolute inset-0 rounded-full blur-sm"
          aria-hidden="true"
        />
      )}
      {/* Optimized image with progressive loading */}
      <img
        ref={imgRef}
        src={mobileImageUrl}
        srcSet={srcSet}
        sizes={sizes}
        alt={displayName || 'User avatar'}
        className={cn(
          'absolute inset-0 rounded-full transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0'
        )}
        loading={priority ? 'eager' : 'lazy'}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};

export const NarrativeAuthorAvatar = ({
  owner,
  size = 'sm',
  className,
  priority = false,
}: Props) => {
  const { displayName, imageUrl } = owner;
  const imageSizes = getImageSizes(size);
  const {
    mobile: mobileSize,
    tablet: tabletSize,
    desktop: desktopSize,
  } = imageSizes;

  const initials = generateInitials(displayName);
  const hasAvatar = hasValidAvatar(imageUrl);

  return (
    <div className={avatar({ size, className })}>
      <div className="text-center text-solid">{initials}</div>
      {hasAvatar && imageUrl && (
        <ProgressiveImage
          imageUrl={imageUrl}
          mobileSize={mobileSize}
          tabletSize={tabletSize}
          desktopSize={desktopSize}
          displayName={displayName}
          priority={priority}
        />
      )}
    </div>
  );
};
